import { z } from 'zod';
import { AppLocale } from './locale.js';
/**
 * Step 1 — email verification (PLAN.md §13.1, §18.1).
 *
 * The legacy funnel keyed every subsequent step on an email supplied in the
 * request body, so knowing an address was enough to drive someone else's
 * application (findings #2 and #3). Here the address is sent EXACTLY ONCE, on
 * `/auth/otp/send`; everything after that is addressed by an opaque challenge
 * id and then by a session cookie.
 */
/** Trimmed and lowercased before it reaches the OTP service. */
export const Email = z
    .string()
    .trim()
    .min(3)
    .max(254)
    .email('Enter a valid email address.')
    .transform((v) => v.toLowerCase());
export const OtpSendRequest = z.object({
    email: Email,
    /** Cloudflare Turnstile token. Required only when the site enforces it. */
    turnstileToken: z.string().max(4096).optional(),
    locale: AppLocale.optional(),
});
/**
 * Deliberately uniform (finding #17).
 *
 * Nothing here varies with whether the address is already known to the CRM, so
 * the endpoint cannot be used to test whether someone holds an account.
 */
export const OtpSendResponse = z.object({
    challengeId: z.string(),
    expiresInSeconds: z.number().int().positive(),
    /** Attempts remaining on the code, so the UI can warn before the lockout. */
    attemptsRemaining: z.number().int().nonnegative(),
    /** True when another provider exists behind the "didn't receive it" button. */
    canEscalate: z.boolean(),
});
export const OtpResendRequest = z.object({
    challengeId: z.string().min(1).max(64),
    /**
     * The "I didn't receive it" path: move to the NEXT provider rather than
     * asking the one that already claimed success to try again (PLAN.md §8).
     */
    escalate: z.boolean().optional(),
    turnstileToken: z.string().max(4096).optional(),
    locale: AppLocale.optional(),
});
export const OtpVerifyRequest = z.object({
    challengeId: z.string().min(1).max(64),
    pin: z
        .string()
        .trim()
        .regex(/^\d{6}$/, 'Enter the 6-digit code.'),
});
export const OtpVerifyResponse = z.object({
    /** The step the session is now on. The client never chooses this. */
    step: z.number().int().min(1).max(4),
    /** Echoed so a returning user resumes where they left off (§11.3). */
    resumed: z.boolean(),
});
//# sourceMappingURL=auth.js.map